/**
 * System Saved Search Templates
 *
 * Pre-built filter presets for common use cases.
 * Users can copy these templates to create their own saved searches.
 *
 * @see Issue #400 - System Templates for First-Time User Experience
 */

import type { SavedSearchTemplate } from './types';

// =============================================================================
// SAVED SEARCH TEMPLATE IDs
// =============================================================================

export const SAVED_SEARCH_TEMPLATE_IDS = {
  HOT_LEADS: 'tpl-search-hot-leads',
  STALLED_APPLICATIONS: 'tpl-search-stalled-applications',
  CLOSING_THIS_WEEK: 'tpl-search-closing-this-week',
  HIGH_VALUE_DEALS: 'tpl-search-high-value-deals',
  NEEDS_FOLLOW_UP: 'tpl-search-needs-follow-up',
} as const;

// =============================================================================
// SAVED SEARCH TEMPLATES
// =============================================================================

export const SAVED_SEARCH_TEMPLATES: SavedSearchTemplate[] = [
  // ---------------------------------------------------------------------------
  // Hot Leads
  // ---------------------------------------------------------------------------
  {
    id: SAVED_SEARCH_TEMPLATE_IDS.HOT_LEADS,
    name: 'Hot Leads',
    description:
      'Leads with high engagement scores who are ready to convert. Focus your efforts on the most promising opportunities.',
    category: 'pipeline',
    icon: 'local_fire_department',
    color: '#F44336',
    tags: ['leads', 'hot', 'high-priority', 'conversion', 'engagement'],
    complexity: 'beginner',
    industryFit: ['real_estate', 'lending', 'mortgage', 'general'],
    data: {
      entity: 'lead',
      filters: [
        {
          field: 'status',
          operator: 'in',
          value: ['QUALIFIED', 'ENGAGED'],
        },
        {
          field: 'engagementScore',
          operator: 'greater_than',
          value: 75,
        },
        {
          field: 'lastActivityAt',
          operator: 'greater_than',
          value: '{{today-7}}',
        },
      ],
      sorts: [
        { field: 'engagementScore', direction: 'desc' },
        { field: 'lastActivityAt', direction: 'desc' },
      ],
      isPinned: true,
    },
  },

  // ---------------------------------------------------------------------------
  // Stalled Applications
  // ---------------------------------------------------------------------------
  {
    id: SAVED_SEARCH_TEMPLATE_IDS.STALLED_APPLICATIONS,
    name: 'Stalled Applications',
    description:
      "Applications that haven't progressed in over 5 days. Identify bottlenecks and re-engage borrowers before they go cold.",
    category: 'pipeline',
    icon: 'pause_circle',
    color: '#FF9800',
    tags: ['applications', 'stalled', 'follow-up', 'bottleneck', 'at-risk'],
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'fintech'],
    data: {
      entity: 'case',
      filters: [
        {
          field: 'status',
          operator: 'in',
          value: ['IN_PROGRESS', 'PENDING'],
        },
        {
          field: 'pipelineStage',
          operator: 'not_in',
          value: ['FUNDED', 'CLOSED', 'WITHDRAWN', 'DENIED'],
        },
        {
          field: 'updatedAt',
          operator: 'less_than',
          value: '{{today-5}}',
        },
      ],
      sorts: [
        { field: 'updatedAt', direction: 'asc' },
        { field: 'loanAmount', direction: 'desc' },
      ],
      isPinned: true,
    },
  },

  // ---------------------------------------------------------------------------
  // Closing This Week
  // ---------------------------------------------------------------------------
  {
    id: SAVED_SEARCH_TEMPLATE_IDS.CLOSING_THIS_WEEK,
    name: 'Closing This Week',
    description:
      'Cases scheduled to close within the next 7 days. Stay on top of your pipeline and ensure smooth closings.',
    category: 'pipeline',
    icon: 'event_available',
    color: '#4CAF50',
    tags: ['closing', 'this-week', 'pipeline', 'scheduled', 'upcoming'],
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'real_estate'],
    data: {
      entity: 'case',
      filters: [
        {
          field: 'pipelineStage',
          operator: 'in',
          value: ['CLEAR_TO_CLOSE', 'CLOSING', 'SCHEDULED'],
        },
        {
          field: 'targetCloseDate',
          operator: 'between',
          value: '{{today}}',
          secondValue: '{{today+7}}',
        },
      ],
      sorts: [
        { field: 'targetCloseDate', direction: 'asc' },
        { field: 'loanAmount', direction: 'desc' },
      ],
      isPinned: true,
    },
  },

  // ---------------------------------------------------------------------------
  // High Value Deals
  // ---------------------------------------------------------------------------
  {
    id: SAVED_SEARCH_TEMPLATE_IDS.HIGH_VALUE_DEALS,
    name: 'High Value Deals ($1M+)',
    description:
      'Cases with loan amounts over $1 million. Prioritize these high-value opportunities for white-glove service.',
    category: 'pipeline',
    icon: 'attach_money',
    color: '#9C27B0',
    tags: ['high-value', 'million', 'priority', 'large-deals', 'vip'],
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'real_estate'],
    data: {
      entity: 'case',
      filters: [
        {
          field: 'loanAmount',
          operator: 'greater_than',
          value: 1000000,
        },
        {
          field: 'status',
          operator: 'not_in',
          value: ['CLOSED', 'CANCELLED', 'WITHDRAWN', 'DENIED'],
        },
      ],
      sorts: [
        { field: 'loanAmount', direction: 'desc' },
        { field: 'priority', direction: 'desc' },
      ],
      isPinned: false,
    },
  },

  // ---------------------------------------------------------------------------
  // Needs Follow-up
  // ---------------------------------------------------------------------------
  {
    id: SAVED_SEARCH_TEMPLATE_IDS.NEEDS_FOLLOW_UP,
    name: 'Needs Follow-up',
    description:
      "Consumers and leads that haven't been contacted in over 14 days. Don't let opportunities slip through the cracks.",
    category: 'engagement',
    icon: 'notification_important',
    color: '#2196F3',
    tags: ['follow-up', 'engagement', 'outreach', 'inactive', 'reminder'],
    complexity: 'beginner',
    industryFit: ['real_estate', 'lending', 'mortgage', 'general'],
    data: {
      entity: 'consumer',
      filters: [
        {
          field: 'status',
          operator: 'equals',
          value: 'ACTIVE',
        },
        {
          field: 'lastContactedAt',
          operator: 'less_than',
          value: '{{today-14}}',
        },
        {
          field: 'hasOpenCase',
          operator: 'equals',
          value: false,
        },
      ],
      sorts: [
        { field: 'lastContactedAt', direction: 'asc' },
        { field: 'totalDealVolume', direction: 'desc' },
      ],
      isPinned: true,
    },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all saved search templates
 */
export function getAllSavedSearchTemplates(): SavedSearchTemplate[] {
  return SAVED_SEARCH_TEMPLATES;
}

/**
 * Get saved search template by ID
 */
export function getSavedSearchTemplateById(id: string): SavedSearchTemplate | undefined {
  return SAVED_SEARCH_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get saved search templates by category
 */
export function getSavedSearchTemplatesByCategory(category: string): SavedSearchTemplate[] {
  return SAVED_SEARCH_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Get saved search templates by entity type
 */
export function getSavedSearchTemplatesByEntity(
  entity: 'case' | 'consumer' | 'partner' | 'lead' | 'task' | 'referral',
): SavedSearchTemplate[] {
  return SAVED_SEARCH_TEMPLATES.filter((t) => t.data.entity === entity);
}

/**
 * Search saved search templates by name, description, or tags
 */
export function searchSavedSearchTemplates(query: string): SavedSearchTemplate[] {
  const lowerQuery = query.toLowerCase();
  return SAVED_SEARCH_TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)),
  );
}
